import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * Status names align with the framework's `approval/event.status` union.
 * The badge is entity-agnostic: timesheets render it today, future
 * entities (expenses, vendor invoices, …) will render the same shape so
 * the UX is consistent.
 *
 * `draft` isn't a framework status — it's the entity-side starting
 * state — but it's listed here because the same badge is used pre- and
 * post-approval. Add new statuses by extending the `Status` union + the
 * `STYLES` map.
 */
export type ApprovalStatusValue = 'draft' | 'submitted' | 'approved' | 'rejected' | 'failed';

/**
 * One source of truth for the visual treatment of every status. Keep
 * colours / labels in sync with `docs/timesheet-state-machine.md` —
 * the operator UX is meaningless if `failed` and `rejected` look
 * identical.
 *
 * `failed` is yellow ("Needs review", system-driven; admin attention)
 * to distinguish it from `rejected` (red; submitter attention).
 * Approved is green; submitted is blue (in-flight); draft is muted.
 *
 * Dark-mode variants on every coloured tone — without them the badges
 * read as a washed-out light blob on a dark background.
 */
const STYLES: Record<ApprovalStatusValue, { label: string; className: string }> = {
  draft: {
    label: 'Draft',
    className: 'bg-muted text-muted-foreground border-border',
  },
  submitted: {
    label: 'Submitted',
    className:
      'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800/60',
  },
  approved: {
    label: 'Approved',
    className:
      'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-200 dark:border-green-800/60',
  },
  rejected: {
    label: 'Rejected',
    className:
      'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800/60',
  },
  failed: {
    label: 'Needs review',
    className:
      'bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-800/60',
  },
};

interface Props {
  status: ApprovalStatusValue;
  /** Populated on rejected/failed. Renders as a tooltip on the badge. */
  reason?: string | null;
  /** `dot` for the corner indicator in the week grid; `pill` (default)
   *  for the day list / detail views. */
  size?: 'pill' | 'dot';
  className?: string;
}

export function ApprovalStatusBadge({ status, reason, size = 'pill', className }: Props) {
  const style = STYLES[status];

  if (size === 'dot') {
    // role="img" + aria-label gives screen readers the status name
    // without requiring visible text. A neutral <span> would reject
    // aria-label (biome a11y/useAriaPropsSupportedByRole).
    const dot = (
      <span
        role="img"
        aria-label={style.label}
        className={cn(
          'inline-block h-2 w-2 rounded-full border',
          style.className,
          'border-current/30',
          className,
        )}
      />
    );
    if (!reason) return dot;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{dot}</TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="font-medium">{style.label}</div>
            <div className="mt-1 text-xs">{reason}</div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const pill = (
    <span
      className={cn(
        'inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium',
        style.className,
        className,
      )}
    >
      {style.label}
    </span>
  );
  if (!reason) return pill;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{pill}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="text-xs">{reason}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
